/**
 * Authentication invalidation — Frontend handler (reason-aware).
 *
 * Three distinct authentication events (see PART 7 of the spec):
 *
 *   1. EXPIRED  — JWT `exp` passed (inactivity / session timeout).
 *                 Backend: 401, detail "TOKEN_EXPIRED", X-Auth-Reason: EXPIRED.
 *                 Also detected proactively client-side by the expiry monitor.
 *   2. SECURITY — password / role / permission changed, or account disabled.
 *                 Backend: 401, X-Token-Stale: 1, X-Auth-Reason: SECURITY.
 *   3. MANUAL   — user clicks Logout (handled in AuthContext, not here).
 *
 * On (1) and (2): clear admin auth state, stash a one-shot message + the
 * current URL (for post-login redirect), then hard-redirect to /agency/login.
 *
 * Framework-agnostic (no React/router) so it is safe to call from the global
 * fetch interceptor and from the client-side expiry monitor.
 */

export const AUTH_REASON = { EXPIRED: 'EXPIRED', SECURITY: 'SECURITY' };

const REASON_MESSAGE = {
  EXPIRED: 'Your session has expired. Please sign in again.',
  SECURITY: 'Your account security or permissions have changed. Please sign in again.',
};

// Backward-compat export (older imports reference this constant).
export const TOKEN_STALE_MESSAGE = REASON_MESSAGE.SECURITY;

// SessionStorage keys the login page reads on mount.
export const TOKEN_STALE_FLAG_KEY = 'funstay_token_stale_msg';
// LocalStorage key holding the URL to return to after re-login.
export const POST_LOGIN_REDIRECT_KEY = 'funstay_post_login_redirect';

// One-shot guard so concurrent failing requests trigger a single redirect.
let _handled = false;

/**
 * Classify a 401 fetch Response into an AUTH_REASON (or null if unrelated).
 * @param {Response} response
 * @param {any} [parsedBody] optional pre-parsed JSON body
 * @returns {'EXPIRED'|'SECURITY'|null}
 */
export const getAuthReason = (response, parsedBody = null) => {
  if (!response || response.status !== 401) return null;
  try {
    const hdr = response.headers && response.headers.get('X-Auth-Reason');
    if (hdr === 'EXPIRED') return AUTH_REASON.EXPIRED;
    if (hdr === 'SECURITY') return AUTH_REASON.SECURITY;
    if (response.headers && response.headers.get('X-Token-Stale') === '1') {
      return AUTH_REASON.SECURITY;
    }
  } catch { /* headers unreadable */ }
  if (parsedBody && typeof parsedBody === 'object') {
    const d = parsedBody.detail || parsedBody.code;
    if (d === 'TOKEN_EXPIRED') return AUTH_REASON.EXPIRED;
    if (d === 'TOKEN_STALE' || d === 'ACCOUNT_INACTIVE') return AUTH_REASON.SECURITY;
  }
  return null;
};

/**
 * Legacy predicate kept for backward compatibility (interceptor + tests).
 * Returns true only for SECURITY invalidations.
 */
export const isTokenStaleResponse = (response, parsedBody = null) =>
  getAuthReason(response, parsedBody) === AUTH_REASON.SECURITY;

const _clearAdminAuthState = () => {
  try {
    localStorage.removeItem('funstay_admin_token');
    localStorage.removeItem('funstay_admin_user');
    localStorage.removeItem('funstay_admin_permissions');
    localStorage.removeItem('funstay_admin_is_super_admin');
    localStorage.removeItem('funstay_admin_primary_position_label');
  } catch { /* best effort */ }
};

/**
 * Main entry point. Idempotent. `reason` is one of AUTH_REASON.
 */
export const handleAuthInvalidation = (reason = AUTH_REASON.SECURITY) => {
  if (_handled) return;
  _handled = true;

  _clearAdminAuthState();

  try {
    sessionStorage.setItem(TOKEN_STALE_FLAG_KEY, REASON_MESSAGE[reason] || REASON_MESSAGE.SECURITY);
  } catch { /* best effort */ }

  // Preserve the current in-app location for a post-login redirect, but only
  // for real agency pages (never the login page itself).
  try {
    if (typeof window !== 'undefined' && window.location) {
      const { pathname, search } = window.location;
      if (pathname.startsWith('/agency') && pathname !== '/agency/login') {
        localStorage.setItem(POST_LOGIN_REDIRECT_KEY, pathname + (search || ''));
      }
    }
  } catch { /* best effort */ }

  if (typeof window !== 'undefined' && window.location) {
    if (window.location.pathname !== '/agency/login') {
      window.location.assign('/agency/login');
    }
  }
};

// Backward-compat wrapper — existing callers default to SECURITY.
export const handleTokenStale = (reason = AUTH_REASON.SECURITY) =>
  handleAuthInvalidation(reason);

export const _resetTokenStaleHandledForTest = () => {
  _handled = false;
};

/**
 * Consume the one-shot message left for the login page (and remove it).
 */
export const consumeTokenStaleMessage = () => {
  try {
    const msg = sessionStorage.getItem(TOKEN_STALE_FLAG_KEY);
    if (msg) sessionStorage.removeItem(TOKEN_STALE_FLAG_KEY);
    return msg || null;
  } catch {
    return null;
  }
};

/**
 * Consume the post-login redirect target (and remove it). Returns a path
 * string or null.
 */
export const consumePostLoginRedirect = () => {
  try {
    const url = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (url) localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return url || null;
  } catch {
    return null;
  }
};

export default handleTokenStale;
